import { useQuery } from 'react-query';
import { useMatch } from 'react-router-dom';
import { Routes, Route, Navigate } from 'react-router-dom';

import config from '~/config';
import type { AppUserState } from '~/hooks/useUser';
import { useAuth } from '~/state/authState';

import CharacterServiceApi from './api/CharacterServiceApi';
import { QueryResponseData } from './listing/typings';
import Listing from './routes/listing';
import SheetBuilderApp from './routes/sheet-builder-app';
import { getUrlWithParams } from './tools/js/CharacterBuilder/utils/navigationUtils';

const BASE_PATHNAME = config.basePathname;

// Code smell remove this after we get stat blocks replaced
(function mountStyles() {
    const style1 = document.createElement('link');
    style1.rel = 'stylesheet';
    style1.href = 'https://www.dndbeyond.com/content/skins/blocks/css/compiled.css';
    const style2 = document.createElement('link');
    style2.rel = 'stylesheet';
    style2.href = 'https://www.dndbeyond.com/content/skins/waterdeep/css/compiled.css';

    document.head.appendChild(style1);
    document.head.appendChild(style2);
    return () => {
        style1.remove();
        style2.remove();
    };
})();

/**
 *
 * RequireAuth checks if you are logged in and redirects to the login page if you are not.
 * To use just wrap your element prop in a Route component in it.
 * For example a the Character Sheet has a AnonymousRoute view so it is not wrapped in a RequireAuth.
 */
function RequireAuth({ children }: { children: JSX.Element }) {
    let user = useAuth();
    const isCharactersListing = useMatch({ path: BASE_PATHNAME, end: true });

    // loading state
    if (user === undefined) {
        return null;
    }
    // not authenticated
    if (user === null) {
        // Should only ever happen in local dev when not using app-shell locally
        // if you remove this it will reveal a memory leak :)
        if (process.env.NODE_ENV === 'development' && isCharactersListing) {
            return <div>{'YOU ARE NOT LOGGED IN. THIS VIEW IS HANDLED BY APP SHELL'}</div>;
        }

        window.location.href = getUrlWithParams();
    }
    // authenticated
    return children;
}

/**
 *
 * GetAllCharacters uses the renderProp pattern to handle requiring getting all characters. when a user is logged in.
 * This ensure that locked users are not able to use this part of the app until the resolved thier locked characters.
 * This was setup this way to avoid having to make a new api call to get the characters when the user is Anonymous.
 * Therefore, keeping our api calls to a minimum and minimizing errors from failed api calls.
 * Note: this could be simpler but complexity was added to make typescript happy.
 */
function GetCallCharacters({ children }: { children: (QueryResponseData) => void }): JSX.Element {
    const isCharactersListing = useMatch({ path: BASE_PATHNAME, end: true });

    const { isLoading, data, refetch, error } = useQuery<QueryResponseData, Error>('repoData', () =>
        CharacterServiceApi.getAllCharacters(),
    );
    if (isLoading) {
        return <>{null}</>;
    }
    if (!error && data?.data?.canUnlockCharacters && !isCharactersListing) {
        return (
            <>
                <Navigate to={BASE_PATHNAME} replace />
            </>
        );
    }
    return <>{children({ isLoading, data, refetch, error })}</>;
}

type AppRoute = {
    path: string;
    element: JSX.Element;
};
const getRoutes = (user: AppUserState): Array<AppRoute> => [
    {
        path: `${BASE_PATHNAME}/builder/*`,
        element: (
            <RequireAuth>
                <GetCallCharacters>
                    {({ data }) => {
                        const numberOfCharacters = data?.data?.characters?.length ?? 0;
                        const maxCharacterSlotsAllowed = data?.data?.maxCharacterSlotsAllowed ?? Infinity;
                        const hasMaxCharacters = numberOfCharacters >= maxCharacterSlotsAllowed;
                        if (hasMaxCharacters) {
                            return <Navigate to={BASE_PATHNAME} replace />;
                        }
                        return <SheetBuilderApp />;
                    }}
                </GetCallCharacters>
            </RequireAuth>
        ),
    },
    {
        path: `${BASE_PATHNAME}/:characterId/builder/*`,
        element: (
            <RequireAuth>
                <GetCallCharacters>{() => <SheetBuilderApp />}</GetCallCharacters>
            </RequireAuth>
        ),
    },
    {
        path: `${BASE_PATHNAME}/:characterId/:shareId`,
        element: user ? <GetCallCharacters>{() => <SheetBuilderApp />}</GetCallCharacters> : <SheetBuilderApp />,
    },
    {
        path: `${BASE_PATHNAME}/:characterId`,
        element: user ? <GetCallCharacters>{() => <SheetBuilderApp />}</GetCallCharacters> : <SheetBuilderApp />,
    },
    {
        path: BASE_PATHNAME,
        element: (
            <RequireAuth>
                <GetCallCharacters>
                    {({ isLoading, data, refetch, error }) => (
                        <Listing characterQuery={{ isLoading, data, refetch, error }} />
                    )}
                </GetCallCharacters>
            </RequireAuth>
        ),
    },
    {
        path: '/',
        element: <Navigate to={BASE_PATHNAME} replace />,
    },
];

function App() {
    const user = useAuth();
    return (
        <Routes>
            {getRoutes(user).map((props: AppRoute) => (
                <Route key={props.path} {...props} />
            ))}
        </Routes>
    );
}

export default App;
